import React from "react";

import { Table } from "src/components/shared/table";
import {
  Column, Filters, SortingRule 
} from "react-table";
import { useApiRequest } from "src/utils/api";

import { Layout } from "src/components/shared/layout/Layout";
import { PageHeader, Card } from "src/components/shared/layout";
import { PageProps } from "src/pages/Router";
import { toast } from "react-toastify";
import { UserDTO, OrderByObject } from "src/utils/api";
import * as datefns from "date-fns";
import { TextInput } from "src/components/forms/TextInput";

type BusinessPartnersTable = Pick<UserDTO, "businessName" | "dateCreated" | "dateLastActivity" | "itemsRegistered" | "itemsReturned" | "id">;

const queryFiltersToString = (filters: Filters<BusinessPartnersTable>): string => {
  return filters.filter(filter => !!filter.value).map(filter => {
    let value = filter.value;

    // User LIKE operator.
    if (filter.id === "businessName" || filter.id === "lastName" || filter.id === "emailAddress") {
      value = `~${value}`;
    } else if (filter.id === "itemsRegistered" || filter.id === "itemsReturned") {
      if (value.charAt(0) === "<" || value.charAt(0) === ">") {
        value = `${value}`;
      } else if (value.charAt(0) === "=") {
        value = `${value.substring(1)}`;
      } else {
        value = `:${value}`;
      }
    } else {
      value = `:${value}`;
    }

    return `${filter.id}${value}`
    ;
  }).join(",");
};

const columns: Column<BusinessPartnersTable>[] = [
  {
    Header: "Id",
    accessor: "id",
    disableFilters: true,
    width: 250
  },
  {
    Header: "Business name",
    accessor: "businessName"
  },
  {
    Header: "Joined",
    accessor: "dateCreated",
    disableFilters: true,
    Cell: ({ value }) => value ? datefns.format(value, "dd/MM/yyyy HH:mm") : ""
  },
  {
    Header: "Last Active",
    accessor: "dateLastActivity",
    disableFilters: true,
    Cell: ({ value }) => value ? datefns.format(value, "dd/MM/yyyy HH:mm") : ""
  },
  {
    Header: "Registered Items",
    accessor: "itemsRegistered",
    // eslint-disable-next-line react/display-name
    Filter: ({ column: { filterValue, setFilter } }) => (
      <TextInput
        placeholder="Use >,< or =. Examples: '=4', '<1', >10"
        value={filterValue}
        onChange={event => setFilter(event.target.value)}
      />
    )   
  },
  {
    Header: "Returned items",
    accessor: "itemsReturned",
    // eslint-disable-next-line react/display-name
    Filter: ({ column: { filterValue, setFilter } }) => (
      <TextInput
        placeholder="Use >,< or =. Examples: '=4', '<1', >10"
        value={filterValue}
        onChange={event => setFilter(event.target.value)}
      />
    )   
  }
];

const hiddenColumns = ["id"];

const BusinessUsersPage: React.FC<PageProps<{}>> = () => {
  const [data, setData] = React.useState<UserDTO[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [pageCount, setPageCount] = React.useState(0);
  const [partnersResponse, partnersRequest] = useApiRequest("USERS:getAllBusinessUsers");
  const fetchIdRef = React.useRef(0);

  const fetchData = React.useCallback(({
    pageSize, pageIndex, sortBy, tableFilters
  }: {
    pageSize: number;
    pageIndex: number;
    tableFilters?: Filters<BusinessPartnersTable>;
    sortBy?: Array<SortingRule<BusinessPartnersTable>>;
  }) => {
    // Give this fetch an ID
    const fetchId = ++fetchIdRef.current;

    if (fetchId === fetchIdRef.current) {
      const filters: Partial<BusinessPartnersTable> = {};
      // Setup order with default, if included:
      let order: OrderByObject<UserDTO> = { dateCreated: "ASC" };

      // Override with sortBy from the table, if included:
      if (sortBy && sortBy[0]) {
        order = { [sortBy[0].id as keyof BusinessPartnersTable]: sortBy[0].desc ? "DESC" : "ASC" };
      }

      tableFilters?.map(filter => {
        const id = filter.id as keyof BusinessPartnersTable;

        filters[id] = filter.value;
      });
      // Set the loading state
      setLoading(true);

      partnersRequest({
        params: {
          query: tableFilters && tableFilters.length ? queryFiltersToString(tableFilters) : "",
          page: pageIndex + 1,
          pageSize,
          sortBy: Object.keys(order).map(sortField => `${sortField}:${order[sortField as keyof UserDTO]}`).join(",")
        }
      });
    }
  }, [partnersRequest]);

  React.useEffect(() => {
    if (partnersResponse.data && partnersResponse.data.items) {
      setData(partnersResponse.data.items);
      setPageCount(Math.ceil(partnersResponse.data.total / partnersResponse.data.pageSize));
      setLoading(false);
    }

    if (partnersResponse.errorMessage) {
      toast.error(partnersResponse.errorMessage);
    }
  }, [partnersResponse]);

  return (
    <Layout>
      <PageHeader title="All business partners" />
      <Card>
        <Table<BusinessPartnersTable>
          name={"Business table"}
          title={`${partnersResponse.data?.total || 0} Business partners`}
          columns={columns}
          data={data}
          loading={loading}
          fetchData={fetchData}
          defaultHiddenColumns={hiddenColumns}
          pageCount={pageCount}
          usePagination={true}
          useFilters={true}
          useHideColumns={true}
          useResizeColumns
          useSortBy
          featuredFilter="businessName"
        />
      </Card>
    </Layout>
  );
};

export default BusinessUsersPage;
